import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, Loader } from './UI';
import SvPlanner from './SvPlanner';
import { getActivities, summarize, ACT } from '../lib/activities';
import { supabase } from '../lib/supabase';
import { mskToday } from '../lib/alerts';

// units: [{ id, label, sub, svIds:[...], children?:[{ id, label, sub, svIds }] }]
// Строка с children (РМ → ТМ) раскрывается и показывает своих СВ; «Открыть» у листа — календарь СВ только для просмотра.
export default function Discipline({ units, period, weeks: weeksProp = [], title = 'Дисциплина СВ' }) {
  const weeks = useMemo(() => [...weeksProp].sort((a, b) => String(a.date_from).localeCompare(String(b.date_from)) || a.week_number - b.week_number), [weeksProp]);
  const today = mskToday();
  const [sel, setSel] = useState('month');
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});
  const [view, setView] = useState(null);

  const allSvIds = useMemo(() => {
    const s = new Set();
    units.forEach(u => (u.svIds || []).forEach(i => s.add(i)));
    return [...s];
  }, [units]);

  const pFrom = period?.date_from, pTo = period?.date_to;

  useEffect(() => {
    if (!pFrom || !pTo) return;
    let off = false;
    setLoading(true); setErr('');
    getActivities(allSvIds, pFrom, pTo)
      .then(r => { if (!off) setActs(r); })
      .catch(e => { if (!off) setErr(e.message); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [allSvIds, pFrom, pTo]);

  const range = useMemo(() => {
    if (sel === 'month') return { from: pFrom, to: pTo };
    const w = weeks.find(x => x.week_number === sel);
    return w ? { from: w.date_from, to: w.date_to } : { from: pFrom, to: pTo };
  }, [sel, weeks, pFrom, pTo]);

  const bySv = useMemo(() => {
    const m = {};
    acts.forEach(a => { (m[a.supervisor_id] = m[a.supervisor_id] || []).push(a); });
    return m;
  }, [acts]);

  // сводка по набору СВ: суммируем сводки каждого СВ (рабочие дни тоже на каждого)
  function sumOf(svIds) {
    const t = { filled: 0, workDays: 0, per: 0, perVisits: 0, sov: 0, asm: 0, sick: 0, st: 0, stm: 0, au: 0, auCnt: 0, of: 0, ot: 0 };
    (svIds || []).forEach(id => {
      const s = summarize(bySv[id] || [], range.from, range.to, today);
      Object.keys(t).forEach(k => { t[k] += s[k]; });
    });
    return t;
  }

  function exportXlsx() {
    const rows = [['Подразделение', 'Уровень', 'Рабочих дней', 'Заполнено дней', '% заполнения', 'Перекрытий', 'Визитов в перекрытиях', 'Совтуров', 'Стажировок МЕ', 'Стажировок МЕ−', 'Аудитов (записей)', 'Аудитов (кол-во)', 'Аудитов с АСМ', 'Офисных дней', 'Отпуск (дней)', 'Больничный (дней)']];
    const push = (u, lvl) => {
      const s = sumOf(u.svIds);
      rows.push([u.label, lvl, s.workDays, s.filled, s.workDays ? Math.round(s.filled / s.workDays * 100) / 100 : 0, s.per, s.perVisits, s.sov, s.st, s.stm, s.au, s.auCnt, s.asm, s.of, s.ot, s.sick]);
    };
    units.forEach(u => { push(u, u.children ? 'ТМ' : 'СВ'); (u.children || []).forEach(c => push(c, 'СВ')); });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Дисциплина СВ');
    XLSX.writeFile(wb, `дисциплина_св_${range.from}_${range.to}.xlsx`);
  }

  // Выгрузка «одна строка = один день × одна активность» (всё или один ТМ), с учётом выбранного периода.
  async function exportDetail(list, name) {
    try {
      const meta = {};
      (list || units).forEach(u => {
        if (u.children) u.children.forEach(c => (c.svIds || []).forEach(id => { meta[id] = { tm: u.label, sv: c.label }; }));
        else (u.svIds || []).forEach(id => { meta[id] = { tm: title, sv: u.label }; });
      });
      const mine = acts.filter(a => meta[a.supervisor_id] && a.work_date >= range.from && a.work_date <= range.to);
      const rids = [...new Set(mine.map(a => a.route_id).filter(Boolean))];
      const rmap = {};
      for (let i = 0; i < rids.length; i += 150) {
        const { data } = await supabase.from('routes').select('id, internal_code, merchandisers(full_name)').in('id', rids.slice(i, i + 150));
        (data || []).forEach(r => { const m = Array.isArray(r.merchandisers) ? r.merchandisers[0] : r.merchandisers; rmap[r.id] = { code: r.internal_code, mrc: m?.full_name || '' }; });
      }
      const rows = [['ТМ', 'СВ', 'Дата', 'План/Факт', 'Активность', 'Маршрут', 'ФИО МРЧ маршрута', 'С кем / кандидат', 'Количество (визитов)', 'Источник']];
      mine.sort((a, b) => (meta[a.supervisor_id].tm + meta[a.supervisor_id].sv + a.work_date).localeCompare(meta[b.supervisor_id].tm + meta[b.supervisor_id].sv + b.work_date, 'ru'))
        .forEach(a => {
          const m = meta[a.supervisor_id]; const r = a.route_id ? rmap[a.route_id] : null;
          rows.push([m.tm, m.sv, a.work_date, a.kind === 'fact' ? 'Факт' : 'План', ACT[a.activity]?.label || a.activity,
            r?.code || '', r?.mrc || '', a.candidate || '', a.cnt ?? '', a.source === 'file' ? 'файл' : 'вручную']);
        });
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [22, 28, 12, 10, 18, 14, 28, 28, 12, 10].map(w => ({ wch: w }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Активности СВ');
      XLSX.writeFile(wb, `активности_св_${name || 'все'}_${range.from}_${range.to}.xlsx`.replace(/\s+/g, '_'));
    } catch (e) { setErr('Не удалось выгрузить: ' + (e.message || e)); }
  }

  const pill = (active) => ({ fontSize: 12, fontWeight: active ? 700 : 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
    border: active ? 'none' : '1px solid var(--border)', background: active ? 'var(--accent)' : '#fff', color: active ? '#fff' : 'var(--text)' });
  const grid = '1.4fr 1.5fr .8fr .55fr .55fr .65fr .55fr .55fr auto';
  const head = { fontSize: 10, fontWeight: 700, color: '#5A6678', textTransform: 'uppercase', letterSpacing: '.04em' };

  function Row({ u, leaf, indent }) {
    const s = sumOf(u.svIds);
    const p = s.workDays ? Math.round(s.filled / s.workDays * 100) : 0;
    const col = p >= 85 ? '#276221' : p >= 60 ? '#D69E2E' : '#C0392B';
    const isOpen = !!open[u.id];
    const R = { textAlign: 'right', fontSize: 12.5 };
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, alignItems: 'center', padding: '9px 14px', paddingLeft: 14 + indent, borderBottom: '1px solid #EDF2F7', fontSize: 12.5 }}>
          <div>
            <div style={{ fontWeight: 700 }}>{u.label}</div>
            {u.sub && <div style={{ fontSize: 11, color: '#5A6678' }}>{u.sub}</div>}
            {!leaf && <button onClick={() => exportDetail([u], u.label)} style={{ fontSize: 11, color: '#2E75B6', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600 }}>⬇ Excel по дням</button>}
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#4A5568', marginBottom: 3 }}>{s.filled} из {s.workDays} раб. дней · <b style={{ color: col }}>{p}%</b></div>
            <div style={{ height: 8, background: '#EDF2F7', borderRadius: 4 }}><div style={{ height: 8, width: `${Math.min(100, p)}%`, background: col, borderRadius: 4 }} /></div>
          </div>
          <div style={R}>{s.per} <span style={{ color: '#5A6678', fontSize: 11 }}>({s.perVisits} виз.)</span></div>
          <div style={R}>{s.sov}</div>
          <div style={R}>{s.st + s.stm}</div>
          <div style={R}>{(s.auCnt || s.au) + s.asm}</div>
          <div style={R}>{s.of}</div>
          <div style={R}>{s.ot + s.sick}</div>
          {leaf
            ? <button onClick={() => setView(u)} style={btn}>Открыть</button>
            : <button onClick={() => setOpen(o => ({ ...o, [u.id]: !o[u.id] }))} style={btn}>{isOpen ? 'Свернуть' : 'СВ'}</button>}
        </div>
        {!leaf && isOpen && (u.children || []).map(c => <Row key={c.id} u={c} leaf indent={indent + 16} />)}
      </>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <button style={pill(sel === 'month')} onClick={() => setSel('month')}>Месяц</button>
        {weeks.map(w => <button key={w.week_number} style={pill(sel === w.week_number)} onClick={() => setSel(w.week_number)}>W{w.week_number}</button>)}
        <button onClick={exportXlsx} style={{ ...pill(false), marginLeft: 'auto', fontWeight: 700 }}>Скачать сводку</button>
        <button onClick={() => exportDetail(null, 'все')} disabled={loading} style={{ ...pill(true), fontWeight: 700 }}>⬇ Выгрузить всё по дням</button>
      </div>
      {err && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>{err}</div>}
      <Card>
        <div style={{ padding: '10px 16px', background: '#F7F9FC', borderBottom: '1px solid #E2E8F0', fontSize: 13, fontWeight: 700 }}>
          {title}
          <span style={{ fontSize: 11, fontWeight: 400, color: '#5A6678', marginLeft: 8 }}>заполнен день = есть любая активность (факт или план)</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 700 }}>
            <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '8px 14px', borderBottom: '1px solid #E2E8F0', ...head }}>
              <div>{units.some(u => u.children) ? 'ТМ / СВ' : 'СВ'}</div><div>Заполнено</div><div style={{ textAlign: 'right' }}>Перекр.</div>
              <div style={{ textAlign: 'right' }}>Совт.</div><div style={{ textAlign: 'right' }}>Стаж.</div><div style={{ textAlign: 'right' }}>Аудитов</div><div style={{ textAlign: 'right' }}>Офис</div><div style={{ textAlign: 'right' }}>Отп./бол.</div><div style={{ width: 74 }} />
            </div>
            {loading ? <Loader /> : units.length === 0
              ? <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>Нет данных.</div>
              : units.map(u => <Row key={u.id} u={u} leaf={!u.children} indent={0} />)}
          </div>
        </div>
      </Card>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Рабочие дни — пн–пт до сегодняшнего дня. Для дня берётся факт, если он есть, иначе план. Это только учёт активностей, на KPI и мотивацию не влияет.
      </div>

      {view && (
        <div onClick={() => setView(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, overflowY: 'auto', padding: '24px 12px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg, #F0F2F5)', maxWidth: 760, margin: '0 auto', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{view.label}</div>
              <button onClick={() => setView(null)} style={btn}>Закрыть</button>
            </div>
            <SvPlanner svId={view.svIds[0]} readOnly svName={view.label} />
          </div>
        </div>
      )}
    </div>
  );
}

const btn = { fontSize: 12, fontWeight: 600, color: '#1F5F9C', background: '#EBF4FF', border: '1px solid #BBD6EE', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', width: 74 };
